import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { printZplChunked, checkBrowserPrint, PrintAborted, ZEBRA_FLUSH_HINT } from "@/lib/zebra";
import { usePrintJob } from "@/lib/usePrintJob";
import { usePrintConfirm } from "@/lib/usePrintConfirm";
import { printerDisplayName } from "@/lib/printerMap";
import { PrintStopBanner } from "@/components/PrintStopBanner";
import { getPrintChunkSize } from "@/lib/printSettings";
import { toast } from "sonner";
import { notifyPdfNoStamp } from "@/lib/printNotice";
import { Printer, FileText, Wifi, WifiOff, RefreshCw, QrCode, Loader2 } from "lucide-react";

const VERSIONS = [
  { key: "A", title: "Version A — Sticker 4×3", desc: "Full detail on an adhesive sticker." },
  { key: "B", title: "Version B — Hang tag 4×6", desc: "Full detail with hang-hole keep-out." },
  { key: "C", title: "Version C — Barcode 3×1", desc: "Compact: stock #, Year/Make/Model, QR + barcode. Full VIN stays on A/B." },
];

// Batch print donor/truck DNR tags for a SELECTED set of trucks. Media type (A/B) is
// picked ONCE for the whole run. Same layout/QR/token rules as the single tag.
export function DonorBatchTagDialog({ open, onOpenChange, donorIds = [], previewItems = null, onPrinted }) {
  const [version, setVersion] = useState("A");
  const [zebra, setZebra] = useState({ ok: null });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [deselected, setDeselected] = useState({});
  const [remainderIds, setRemainderIds] = useState(null);   // run-scoped: unsent ids after an interrupted run
  const printJob = usePrintJob();
  const { printConfirmDialog, confirmAndRun } = usePrintConfirm();

  useEffect(() => { if (open) { checkBrowserPrint().then(setZebra); setProgress(null); setDeselected({}); setRemainderIds(null); } }, [open]);

  const hasPreview = Array.isArray(previewItems) && previewItems.length > 0;
  const baseIds = hasPreview ? previewItems.filter((i) => !deselected[i.id]).map((i) => i.id) : donorIds;
  const effectiveIds = remainderIds ?? baseIds;
  const n = effectiveIds.length;

  const printZebra = () => {
    if (busy) return;
    confirmAndRun({ title: `Print ${n} truck tag${n === 1 ? "" : "s"}`, labelCount: n, copies: 1,
                    noun: `Version ${version} truck tags`, mediaType: { A: "sticker", B: "hang_tag", C: "barcode_label" }[version] }, async () => {
      setBusy(true); setProgress({ done: 0, total: n });
      const tid = toast.loading(`Preparing ${n} truck tag${n === 1 ? "" : "s"}…`);
      try {
        const { data } = await api.post("/donors/tags/zpl", { donor_ids: effectiveIds, version });
        const printable = (data.items || []).filter((i) => i.zpl);
        const zplList = printable.map((i) => i.zpl);
        if (!zplList.length) { toast.error("No tags to print.", { id: tid }); return; }
        const signal = printJob.start(`${data.count} truck tags · Version ${version}`, zplList.length);
        try {
          const dev = await printZplChunked(zplList, { chunkSize: getPrintChunkSize(), onProgress: (done, total) => {
            setProgress({ done, total }); printJob.progress(done, total);
            toast.loading(`Printing ${done}/${total}…`, { id: tid });
          }, signal, mediaType: { A: "sticker", B: "hang_tag", C: "barcode_label" }[version] });
          // confirm-driven stamp: reached only if the run finished without abort/throw —
          // stamp exactly the trucks whose tag was actually built & sent
          await api.post("/donors/tags/label-printed/confirm", { donor_ids: printable.map((i) => i.donor_id), version }).catch(() => {});
          toast.success(`Sent ${data.count} truck tag${data.count === 1 ? "" : "s"} (Version ${version}) to ${printerDisplayName(dev)}.`, { id: tid });
          if (data.skipped?.length) toast.warning(`${data.skipped.length} selected truck(s) were skipped (not found).`);
          setRemainderIds(null);
          onPrinted?.();
          onOpenChange(false);
        } catch (e) {
          if (e instanceof PrintAborted || e.aborted) {
            // honest partial: the tags that fully went out ARE printed — record exactly those
            const sentIds = printable.slice(0, e.done ?? 0).map((i) => i.donor_id).filter(Boolean);
            if (sentIds.length) await api.post("/donors/tags/label-printed/confirm", { donor_ids: sentIds, version }).catch(() => {});
            // run-scoped remainder (same mechanism as Make Tags batch recovery): keep the
            // dialog open with ONLY the unsent trucks queued for the next Print press
            const restIds = printable.slice(e.done ?? 0).map((i) => i.donor_id).filter(Boolean);
            setRemainderIds(restIds.length ? restIds : null);
            toast.error(`Stopped after ${e.done ?? 0} of ${e.total ?? zplList.length} — the ${sentIds.length} that fully printed are recorded as printed. ${ZEBRA_FLUSH_HINT}`, { id: tid, duration: 12000 });
            onPrinted?.();
          }
          else toast.error(`Zebra Browser Print not reachable — use the PDF fallback. (${e.message})`, { id: tid });
        } finally { printJob.end(); }
      } catch (e) {
        const detail = e?.response?.data?.detail;
        toast.error(detail ? formatApiErrorDetail(detail) : `Could not build the truck tags. (${e.message})`, { id: tid });
      } finally { setBusy(false); setProgress(null); }
    });
  };

  const openPdf = async () => {
    if (busy) return;
    if (!window.confirm(`Open a PDF with ${n} truck tag${n === 1 ? "" : "s"} (Version ${version})? Print it at Actual Size / 100%.`)) return;
    setBusy(true);
    try {
      const res = await api.post("/donors/tags/pdf", { donor_ids: effectiveIds, version }, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
      notifyPdfNoStamp();
      toast.message(`PDF with ${n} tag${n === 1 ? "" : "s"} opened.`, { description: "Print at Actual Size / 100% — NOT Fit to Page." });
      onPrinted?.();
    } catch (e) { toast.error("Could not build the batch tag PDF."); }
    finally { setBusy(false); }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="donor-batch-tag-dialog" className="rounded-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5 text-[#B01E28]" />Print {n} truck tag{n === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>
            QR + barcode + DNR#, one tag per truck. Pick ONE media type for the whole run, then send to the Zebra or open the combined PDF.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {VERSIONS.map((v) => (
            <button key={v.key} data-testid={`donor-batch-version-${v.key}`} onClick={() => !remainderIds && setVersion(v.key)}
                    disabled={!!remainderIds}
              className={`w-full text-left rounded-lg border p-3 transition-all ${version === v.key ? "border-[#B01E28] bg-red-50/60 ring-1 ring-[#B01E28]" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="flex items-center gap-2">
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${version === v.key ? "border-[#B01E28]" : "border-slate-300"}`}>
                  {version === v.key && <span className="w-2 h-2 rounded-full bg-[#B01E28]" />}
                </span>
                <span className="font-display font-bold text-sm text-slate-800">{v.title}</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5 ml-6">{v.desc}</div>
            </button>
          ))}
        </div>

        {hasPreview && (
          <div data-testid="donor-batch-preview" className="border border-slate-200 rounded-lg">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-lg">
              <span className="text-xs font-semibold text-slate-600">Trucks to tag <span data-testid="donor-batch-preview-count" className="font-mono">({n})</span></span>
              <div className="flex gap-2 text-[11px]">
                <button type="button" data-testid="donor-batch-preview-all" onClick={() => setDeselected({})} className="text-[#E0781A] hover:underline">Select all</button>
                <span className="text-slate-300">|</span>
                <button type="button" data-testid="donor-batch-preview-none" onClick={() => setDeselected(Object.fromEntries(previewItems.map((i) => [i.id, true])))} className="text-slate-500 hover:underline">Clear</button>
              </div>
            </div>
            <div className="max-h-48 overflow-auto divide-y divide-slate-100">
              {previewItems.map((it) => (
                <label key={it.id} data-testid={`donor-batch-preview-item-${it.stock_number}`} className="flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={!deselected[it.id]} onChange={(e) => setDeselected((s) => ({ ...s, [it.id]: !e.target.checked }))} className="w-4 h-4 accent-[#C76714] cursor-pointer" />
                  <span className="font-mono text-xs font-bold text-[#B01E28]">{it.stock_number || "(no stock #)"}</span>
                  {[it.year, it.make, it.model].filter(Boolean).length > 0 && <span className="text-xs text-slate-500 truncate">{[it.year, it.make, it.model].filter(Boolean).join(" ")}</span>}
                </label>
              ))}
            </div>
          </div>
        )}

        {remainderIds && (
          <div data-testid="donor-batch-remainder" className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 flex items-center justify-between gap-2">
            <span><b>Run stopped</b> — {remainderIds.length} tag{remainderIds.length === 1 ? "" : "s"} never printed. <b>Print</b> now sends only those, locked to Version {version}.</span>
            <button type="button" data-testid="donor-batch-remainder-clear" onClick={() => setRemainderIds(null)} className="underline shrink-0">reset to full selection</button>
          </div>
        )}

        <div data-testid="donor-batch-zebra-status" className={`border rounded-lg p-2.5 flex items-center justify-between text-xs ${zebra.ok ? "border-green-300 bg-green-50" : zebra.ok === false ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
          <span className="flex items-center gap-1.5">
            {zebra.ok ? <Wifi className="w-3.5 h-3.5 text-green-600" /> : <WifiOff className="w-3.5 h-3.5 text-amber-600" />}
            {zebra.ok === null ? "Checking Zebra Browser Print…" : zebra.ok ? `Zebra connected — ${zebra.count} printer${zebra.count === 1 ? "" : "s"} (destination is shown when you confirm)` : "Zebra not detected — use the PDF fallback (print at 100%)."}
          </span>
          <button data-testid="donor-batch-zebra-recheck" onClick={async () => { setZebra({ ok: null }); setZebra(await checkBrowserPrint()); }} className="text-slate-500 hover:text-[#B01E28] flex items-center gap-1"><RefreshCw className="w-3 h-3" />Recheck</button>
        </div>

        {progress && (
          <div data-testid="donor-batch-progress" className="text-xs font-mono text-slate-600 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />Printing {progress.done} / {progress.total}…
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button data-testid="donor-batch-print-zebra" disabled={busy || n === 0} onClick={printZebra}
            className="flex-1 rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white"><Printer className="w-4 h-4 mr-1.5" />Print {n} to Zebra</Button>
          <Button data-testid="donor-batch-print-pdf" disabled={busy || n === 0} variant="outline" onClick={openPdf}
            className="flex-1 rounded-lg border-slate-300"><FileText className="w-4 h-4 mr-1.5" />Download PDF ({n})</Button>
        </div>
      </DialogContent>
    </Dialog>
    {printConfirmDialog}
    <PrintStopBanner job={printJob.job} onStop={printJob.stop} />
    </>
  );
}
